import { useState } from 'react';
import { X, MessageCircle, Loader2, Send, Check } from 'lucide-react';
import { supabase, authService } from '../lib/supabase';

interface ModMessageModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function ModMessageModal({ isOpen, onClose }: ModMessageModalProps) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!subject.trim() || !message.trim()) {
            setError('Please fill in both subject and message');
            return;
        }

        setSending(true);
        setError('');

        try {
            const user = await authService.getCurrentUser();
            if (!user) {
                setError('Please sign in to send a message');
                return;
            }

            const { error: insertError } = await supabase
                .from('mod_messages')
                .insert({
                    user_id: user.id,
                    subject: subject.trim(),
                    message: message.trim(),
                    status: 'unread',
                });

            if (insertError) throw insertError;

            setSent(true);
            setTimeout(() => {
                onClose();
                // Reset form
                setSubject('');
                setMessage('');
                setSent(false);
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-500/20 rounded-lg">
                            <MessageCircle className="size-5 text-purple-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">Message Mod Team</h2>
                            <p className="text-xs text-white/50">Send a message to the moderators</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                        <X className="size-5 text-white/60" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    {sent ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="size-8 text-green-400" />
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">Message Sent!</h3>
                            <p className="text-white/50 text-sm">The mod team will review your message.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm text-white/60 mb-2">Subject</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="What's this about?"
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500/50"
                                    maxLength={100}
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-white/60 mb-2">Message</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Describe your issue or question..."
                                    rows={5}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500/50 resize-none"
                                    maxLength={1000}
                                />
                                <p className="text-xs text-white/30 mt-1">{message.length}/1000</p>
                            </div>

                            {error && (
                                <p className="text-red-400 text-sm">{error}</p>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 py-3 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending || !subject.trim() || !message.trim()}
                                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    {sending ? (
                                        <Loader2 className="size-4 animate-spin" />
                                    ) : (
                                        <Send className="size-4" />
                                    )}
                                    Send Message
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 pb-4">
                    <p className="text-xs text-white/30 text-center">
                        Your message will be reviewed by the DECKSTR.GG mod team. We'll get back to you as soon as possible.
                    </p>
                </div>
            </div>
        </div>
    );
}
